import React from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { TransferOrderLineDTO } from '@recurrency/core-api-schema/dist/transferOrderLines/getTransferOrderLines';
import { theme } from 'theme';

import { DEFAULT_NUM_ROWS_SHOWN, MiniTable } from 'components/MiniTable';
import { ViewAllModal } from 'components/recipes/sidePane/ViewAllModal';
import { TransferOrderLinesTable } from 'components/recipes/TransferOrderLinesTable';
import { DEFAULT_PAGE_SIZE } from 'components/Table';

import { useCoreApi } from 'hooks/useApi';

import { showAsyncModal } from 'utils/asyncModal';
import { formatDateShorthand } from 'utils/formatting';

import { QtyByMonthSparkline } from './QtyByMonthSparkline';

function TransferOrderLinesSection({
  title,
  items,
  isLoading,
  error,
  locationId,
  locationName,
  itemId,
  itemCode,
  direction,
}: {
  title: string;
  items: TransferOrderLineDTO[] | undefined;
  isLoading: boolean;
  error: Error | undefined;
  locationId: string;
  locationName: string;
  itemId: string;
  itemCode: string;
  direction: 'inbound' | 'outbound';
}) {
  return (
    <div
      className={css`
        &:not(:first-child) {
          margin-top: 16px;
        }
      `}
    >
      <div
        className={css`
          ${theme.typography.captionSpaced};
          color: ${theme.colors.neutral[700]};
          margin-bottom: 4px;
          text-transform: uppercase;
        `}
      >
        {title}
      </div>
      <MiniTable
        numRowsShown={DEFAULT_NUM_ROWS_SHOWN}
        data={items}
        columns={[
          { render: (record: TransferOrderLineDTO) => formatDateShorthand(record.transferDate), width: '90px' },
          {
            render: (record: TransferOrderLineDTO) =>
              direction === 'inbound'
                ? `${record.fromLocationId}: ${record.fromLocationName ?? ''}`.trim()
                : `${record.toLocationId}: ${record.toLocationName ?? ''}`.trim(),
          },
          {
            render: (record: TransferOrderLineDTO) => `${record.quantityToTransfer} ${record.unitOfMeasure}`,
            align: 'right',
          },
        ]}
        isLoading={isLoading}
        error={error}
        onMoreButtonClick={() => {
          showAsyncModal(ViewAllModal, {
            title: `${title} for Item ${itemCode} at Location ${locationId}: ${locationName}`,
            content: <TransferOrderLinesTable itemId={itemId} locationId={locationId} direction={direction} />,
          });
        }}
      />
    </div>
  );
}

export function TransferOrderLinesPanelContent({
  itemId,
  itemCode,
  locationId,
  locationName,
}: {
  itemId: string;
  itemCode: string;
  locationId: string;
  locationName: string;
}) {
  const { data, isLoading, error } = useCoreApi(schemas.transferOrderLines.getTransferOrderLines, {
    queryParams: {
      offset: 0,
      limit: DEFAULT_PAGE_SIZE,
      filter: { itemId, locationId },
    },
  });

  const importItems = data?.items.filter((line) => line.toLocationId === locationId);
  const exportItems = data?.items.filter((line) => line.fromLocationId === locationId);

  const monthlyAggs = React.useMemo(() => {
    if (!data?.items) return [];
    const monthlyTotals: Record<string, number> = {};
    for (const line of data.items) {
      const month = `${line.transferDate.slice(0, 7)}-01`;
      const isExport = line.fromLocationId === locationId;
      const sign = isExport ? -1 : 1;
      monthlyTotals[month] = (monthlyTotals[month] || 0) + sign * line.quantityToTransfer;
    }
    return Object.entries(monthlyTotals).map(([transferDate, quantityToTransfer]) => ({
      transferDate,
      quantityToTransfer,
    }));
  }, [data?.items, locationId]);

  return (
    <>
      <QtyByMonthSparkline
        data={monthlyAggs}
        dateField="transferDate"
        quantityField="quantityToTransfer"
        seriesTitle="Qty to Transfer"
      />
      <TransferOrderLinesSection
        title="Inbound Transfers"
        items={importItems}
        isLoading={isLoading}
        error={error}
        itemId={itemId}
        itemCode={itemCode}
        locationId={locationId}
        locationName={locationName}
        direction="inbound"
      />
      <TransferOrderLinesSection
        title="Outbound Transfers"
        items={exportItems}
        isLoading={isLoading}
        error={error}
        itemId={itemId}
        itemCode={itemCode}
        locationId={locationId}
        locationName={locationName}
        direction="outbound"
      />
    </>
  );
}
